/* eslint-disable no-console, no-underscore-dangle */
import {appConfig} from '~/config';
import {initSentry} from '~/services/sentry';
import * as Sentry from '@sentry/react';
import * as storage from '~/utils/storage';
import {Axios, AxiosError} from 'axios';

type AppErrType = Error | AxiosError<any>;

interface ErrorOptions {
  shouldReport?: boolean;
  debugMessage?: string;
}

export async function reportToSentry(err: AppErrType, debugMsg = '') {
  if (!Sentry.getCurrentHub().getClient()) {
    initSentry();
  }
  const user = await storage.load('EASE_WEB_MEDICAL_USER');
  if (user) {
    Sentry.setUser(user);
    Sentry.setTag('email', user?.email);
  }
  Sentry.setExtra('debugMsg', debugMsg);
  Sentry.captureException(err);
}

function isAxiosErr(anyError: AppErrType): anyError is AxiosError {
  return !!((anyError as AxiosError)?.response || (anyError as AxiosError)?.request);
}
export default class AppError {
  userMsg?: string;

  message?: string; // just an alias for userMsg

  err: AppErrType;

  originalErrMsg: string;

  debugMsg: string;

  isAxiosErr: boolean;

  customErrCode: string | null;

  messageBag: null | {[key: string]: any};

  contexts: {[key: string]: any} | undefined;

  statusCode?: number;

  isOffline?: boolean;

  constructor(
    err: AppErrType | unknown,
    userMessage = '',
    {debugMessage = '', shouldReport = true}: ErrorOptions = {},
  ) {
    if (!(err instanceof Axios) && !(err instanceof Error)) {
      throw new Error('Wrong type of App Error');
    }
    this.err = err as AppErrType;
    this.originalErrMsg = isAxiosErr(this.err) ? this.err.message : '';
    this.debugMsg = debugMessage || 'Error!';
    this.isAxiosErr = isAxiosErr(this.err);
    this.customErrCode = null;
    this.messageBag = null;
    this.contexts = undefined;
    this.statusCode = isAxiosErr(this.err) ? this.err?.response?.status : 500;
    this.handleErr(userMessage, shouldReport);
    this.isOffline = false;
  }

  handleErr = (userMessage: string, shouldReport: boolean) => {
    this.userMsg = this.originalErrMsg;
    this.isOffline = this.isNetworkError(this.err);
    if (this.isOffline) {
      alert('Oops, something went wrong with your internet connection');
    }
    if (isAxiosErr(this.err)) {
      this.userMsg = this.err?.response?.data?.message || this.userMsg;
    }
    this.userMsg = userMessage || this.userMsg;
    this.message = this.userMsg;
    console.log('---------Debug:---------');
    console.log(this.debugMsg);
    this.printLog(this.err);
    if (this.isOffline || this.statusCode === 422 || this.statusCode === 404) {
      shouldReport = false;
    }
    if (appConfig.sentry.isEnable && shouldReport) {
      reportToSentry(this.err, this.debugMsg).then(r => r);
    }
  };

  /*
    - https://github.com/axios/axios/issues/383
    - https://github.com/axios/axios/pull/1419
  */
  isNetworkError = (error: AppErrType) => !!isAxiosErr(error) && !error.response;

  printLog = (error: AppErrType) => {
    if (isAxiosErr(error)) {
      const axiosResponse = error?.response;
      if (axiosResponse) {
        /*
         * The request was made and the server responded with a
         * status code that falls out of the range of 2xx
         */
        this.customErrCode = axiosResponse?.data.statusCode;
        this.messageBag = axiosResponse?.data.errors;
        this.contexts = axiosResponse?.data.fields;
        this.statusCode = axiosResponse?.status;
        const {url, data, method} = axiosResponse?.config || {};
        const requestInfo = {
          method,
          url,
          data,
        };
        console.log('Response headers:');
        console.log(axiosResponse?.headers);
        console.log('Response body:');
        console.log(axiosResponse?.data);
        console.log('Request info:');
        console.log(requestInfo);
        Sentry.setExtra('error_header', axiosResponse?.headers);
        Sentry.setExtra('error_data', axiosResponse?.data);
        Sentry.setExtra('request_info', requestInfo);
      } else if (error?.request) {
        /*
         * The request was made but no response was received, `error.request`
         * is an instance of XMLHttpRequest in the browser and an instance
         * of http.ClientRequest in Node.js
         */
        console.log('Axios requested but no response is returned!');
        console.log(error.request._response);
        console.log(error.request);
        Sentry.setExtra('error_request', error.request);
        Sentry.setExtra('error_request_response', error.request._response);
      }
    } else {
      console.log(error);
      Sentry.setExtra('error', error);
    }
  };
}
